import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from 'axios';
import { getAdminByToken } from "./_request";
import { liveURL } from "../constants/Consts";
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import shrineBg from '../../assets/images/shrine_bg.jpg';
import logo from '../../assets/images/logo.png';

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordType, setPasswordType] = useState("password");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const LOGIN_URL = `${liveURL}/v1/adminPortal/login/admin`;

  const handleTogglePass = () => {
    setPasswordType(passwordType === "password" ? "text" : "password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both Email and Password.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await axios.post(LOGIN_URL, {
        email,
        password,
      }, {
        withCredentials: true,
      });

      if (response.data.token) {    
        const userToken = response.data.token;
        const adminResponse = await getAdminByToken(userToken);
        if (adminResponse.Result) {
          login(userToken, adminResponse.data);
          navigate("/dashboard");
        } else {
          setError("Admin data could not be fetched.");
        }
      } else {
        setError("Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error(error);
      setError("Invalid login credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="h-screen w-screen relative overflow-hidden flex items-center justify-center bg-slate-950"
      style={{
        backgroundImage: `url(${shrineBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 40%',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Ambient background overlay */}
      <div className="absolute inset-0 bg-slate-950/20 backdrop-brightness-95 pointer-events-none" />

      {/* Main Single Unified Shrine Gate Card Container */}
      <div className="relative z-10 w-full max-w-[330px] mx-4 mt-20 transform translate-y-6">
        
        {/* SVG Unified Background & Double Border Frame */}
        <div className="absolute inset-0 w-full h-full pointer-events-none">
          <svg 
            viewBox="19 -25 310 300" 
            className="w-full h-full drop-shadow-2xl overflow-visible"
            preserveAspectRatio="none"
          >
            {/* Outer Marble Surface & Gold Border */}
            <path 
              d="M 170 -22 C 130 12 0 25 0 95 L 0 465 C 0 480 12 490 28 490 L 312 490 C 328 490 340 480 340 465 L 340 95 C 340 25 210 12 170 -22 Z" 
              fill="#fdf6e3" 
              stroke="#b48c4b" 
              strokeWidth="3.5" 
            />
            {/* Inner Dashed Gold Arabesque Frame */}
            <path 
              d="M 170 -6 C 132 24 12 36 12 98 L 12 455 C 12 466 22 476 33 476 L 307 476 C 318 476 328 466 328 455 L 328 98 C 328 36 208 24 170 -6 Z" 
              fill="none" 
              stroke="#d97706" 
              strokeWidth="1.5" 
              strokeDasharray="4 2"
            />
          </svg>
        </div>

        {/* Unified Card Content */}
        <div className="relative z-10 px-6 pt-12 pb-6 space-y-4">
          
          {/* Header Section */}
          <div className="text-center space-y-1">
            <img src={logo} alt="Logo" className="w-11 h-11 mx-auto object-contain drop-shadow-md mb-0.5" />
            <h1 className="text-xl font-extrabold text-amber-950 tracking-tight">
              Mushtaqan e Noor
            </h1>
            <div className="text-[10px] font-bold tracking-widest uppercase text-amber-800/80 border-t border-b border-amber-800/20 py-0.5 inline-block px-3">
              CMS LOGIN
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="p-2.5 rounded-xl bg-rose-500/15 border border-rose-600/30 text-rose-900 text-xs font-semibold text-center">
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Email Address */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-amber-900/80 px-1">
                Email Address
              </label>
              <div className="relative flex items-center">
                <input
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 pl-9 pr-3 text-xs bg-amber-50/80 border border-amber-700/35 rounded-xl text-amber-950 placeholder-amber-800/40 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition-all font-medium shadow-inner"
                  required
                />
                <EmailIcon className="absolute left-2.5 text-amber-800/60" fontSize="small" />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-amber-900/80 px-1">
                Password
              </label>
              <div className="relative flex items-center">
                <input
                  type={passwordType}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-10 pl-9 pr-9 text-xs bg-amber-50/80 border border-amber-700/35 rounded-xl text-amber-950 placeholder-amber-800/40 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition-all font-medium shadow-inner"
                  required
                />
                <LockIcon className="absolute left-2.5 text-amber-800/60" fontSize="small" />
                <button
                  type="button"
                  onClick={handleTogglePass}
                  className="absolute right-2.5 text-amber-800/60 hover:text-amber-950 transition-colors"
                >
                  {passwordType === "password" ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </button>
              </div>
            </div>

            {/* Blue Action Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className={`w-full h-11 rounded-xl text-white font-bold text-xs uppercase tracking-wider shadow-lg flex items-center justify-center space-x-2 transition-all duration-300 ${
                  loading 
                    ? "bg-blue-700/60 cursor-not-allowed" 
                    : "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 border border-blue-400/40 hover:shadow-xl hover:scale-[1.02]"
                }`}
                style={{
                  boxShadow: '0 4px 15px rgba(37, 99, 235, 0.45)'
                }}
              >
                <span>{loading ? "Loading..." : "Login"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
